import { useState } from 'react';

import { cn } from '../../../lib';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { sortBy } from 'lodash';
import { Button } from '../button';
import { Command, CommandInput, CommandItem, CommandList, cmdkItemValue } from '../command';
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { sanitize } from '@folio/utils';
import { useTranslation } from 'react-i18next';

export type SearchableSelectOption = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
};

export type SearchableSelectProps = {
  name?: string;
  options: SearchableSelectOption[];
  query?: string;
  disabled?: boolean;
  value: string | undefined | null;
  onChange(nextValue: string): void;
  clearable?: boolean;
  searchable?: boolean;
  placeholder?: string;
};

export function SearchableSelect({
  name,
  value,
  options,
  onChange,
  disabled,
  clearable,
  searchable,
  placeholder,
}: SearchableSelectProps) {
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState('');

  const sortedOptions = sortBy(
    options.filter(({ label }) => sanitize(label).includes(sanitize(searchString))),
    ({ label }) => sanitize(label),
  );

  const items = clearable
    ? [{ label: '--- Aucune ---', value: '---' }, ...sortedOptions]
    : sortedOptions;

  const [open, setOpen] = useState(false);

  const selectedOption = value ? options.find(option => option.value === value) : undefined;
  const shouldShowPlaceholder = !value || !selectedOption;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          role="combobox"
          name={name}
          aria-expanded={open}
          className={cn(
            'w-full justify-between font-normal',
            shouldShowPlaceholder && 'text-muted-foreground hover:text-gray-600',
            disabled && 'bg-gray-50 opacity-50',
          )}
        >
          <span className="truncate text-left">
            {shouldShowPlaceholder
              ? (placeholder ?? t('searchableSelect.placeholder'))
              : selectedOption?.label}
          </span>
          <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        alignOffset={0}
        className="p-0 w-[var(--radix-popover-trigger-width)]"
        avoidCollisions={true}
      >
        <Command onValueChange={() => setOpen(false)} shouldFilter={false}>
          {searchable && (
            <CommandInput
              placeholder={t('searchableSelect.search')}
              value={searchString}
              onValueChange={setSearchString}
            />
          )}
          <CommandList>
            {items.map(item => {
              const hasDescription = !!item.description;
              return (
                <CommandItem
                  key={item.value}
                  value={cmdkItemValue(item.value)}
                  disabled={item.disabled}
                  className={cn(hasDescription && 'items-start')}
                  onSelect={() => {
                    onChange(item.value === '---' ? '' : item.value);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn(
                      'mr-2 h-4 w-4 shrink-0',
                      item.value === value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <div className={cn(hasDescription && 'flex flex-col gap-0.5')}>
                    <span>{item.label}</span>
                    {hasDescription && (
                      <span className="text-xs text-muted-foreground">{item.description}</span>
                    )}
                  </div>
                </CommandItem>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
